import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';

import type { ApiErrorBody } from '@/api/axios';
import {
  createPlace as createPlaceSvc,
  deletePlace as deletePlaceSvc,
  updatePlace as updatePlaceSvc,
} from '@/api/services/placesService';
import type { PlaceCreate, PlaceDto, PlaceUpdate } from '@/api/types/Place';

import type { RootState } from './index';

// Places (Adresář) slice — MUTATIONS ONLY (CHR-06 contract §D). The list is
// deliberately NOT held in the store: `PlacesPage` owns it component-local
// because the query is server-side paged + filtered, so a global cache would
// only ever mirror the current toolbar state (same reasoning as
// `AdminUsersPage` / the chr-04 drivers registry).
//
// What the slice DOES own is the invalidation signal: every successful
// mutation bumps `revision`, and any component rendering place data re-fetches
// when it changes. That keeps „uloženo v dialogu → seznam se obnoví" working
// without lifting the list into Redux, and gives the jízda-editor place
// autocomplete (W2) the same free refresh.

export interface PlacesState {
  // Monotonic counter — incremented on every successful place mutation.
  revision: number;
}

const initialState: PlacesState = {
  revision: 0,
};

const placesSlice = createSlice({
  name: 'places',
  initialState,
  reducers: {
    // Marks every place-derived view as stale. Dispatched by the mutation
    // thunks below; also callable directly (e.g. after an OPTIMISTIC_LOCK so
    // the dialog's parent pulls the winning row back in).
    invalidate(state) {
      state.revision += 1;
    },
  },
});

export const { invalidate: invalidatePlaces } = placesSlice.actions;
export const placesReducer = placesSlice.reducer;

// ----- Mutation thunks --------------------------------------------------
//
// Thin wrappers around `placesService` — each dispatches `invalidatePlaces()`
// on success. Failures reject with the api's ADR-0010 error CODE so the caller
// can map it (409 PLACE_NAME_EXISTS → field error on Název, 409
// OPTIMISTIC_LOCK → the shared conflict message) instead of a generic toast.
//
// ⚠ `rejectWithValue` is load-bearing, not decoration: createAsyncThunk's
// default error path runs `miniSerializeError`, which keeps only
// `name/message/stack/code` — and on an axios failure that `code` is axios' own
// (`ERR_BAD_REQUEST`), NOT the api's. Without the explicit reject value the
// thrown error carries no way to tell a duplicate name from a stale version.

export interface PlaceMutationError {
  // ADR-0010 error code (`ErrorCodes.*`), absent on network/CORS failures.
  code?: string;
}

const mutationError = (e: unknown): PlaceMutationError => {
  if (!isAxiosError(e)) return {};
  const code = (e.response?.data as ApiErrorBody | undefined)?.code;
  return code ? { code } : {};
};

export const createPlaceAndInvalidate = createAsyncThunk<
  PlaceDto,
  PlaceCreate,
  { rejectValue: PlaceMutationError }
>('places/create', async (payload, { dispatch, rejectWithValue }) => {
  try {
    const saved = await createPlaceSvc(payload);
    dispatch(invalidatePlaces());
    return saved;
  } catch (e) {
    return rejectWithValue(mutationError(e));
  }
});

export const updatePlaceAndInvalidate = createAsyncThunk<
  PlaceDto,
  { id: string; payload: PlaceUpdate },
  { rejectValue: PlaceMutationError }
>('places/update', async ({ id, payload }, { dispatch, rejectWithValue }) => {
  try {
    const saved = await updatePlaceSvc(id, payload);
    dispatch(invalidatePlaces());
    return saved;
  } catch (e) {
    return rejectWithValue(mutationError(e));
  }
});

export const deletePlaceAndInvalidate = createAsyncThunk<
  string,
  { id: string; version: number },
  { rejectValue: PlaceMutationError }
>('places/delete', async ({ id, version }, { dispatch, rejectWithValue }) => {
  try {
    await deletePlaceSvc(id, version);
    dispatch(invalidatePlaces());
    return id;
  } catch (e) {
    return rejectWithValue(mutationError(e));
  }
});

// ----- Selectors --------------------------------------------------------

export const selectPlacesRevision = (state: RootState): number =>
  state.places.revision;
